import React, { useState } from 'react';
import { fmt } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const PACE_COLORS = {
  under: { bg: 'rgba(0,212,170,0.1)', border: 'rgba(0,212,170,0.3)', text: '#00d4aa', bar: '#00d4aa' },
  even: { bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.3)', text: '#fbbf24', bar: '#fbbf24' },
  over: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#ef4444', bar: '#ef4444' },
};

// A pool of marker glyphs - one is picked at random per row, per page load,
// just for a bit of visual variety
const MARKER_GLYPHS = ['📍', '📌', '🎯', '⚡', '🔥', '💫', '⭐', '🚩', '🔺', '🔻', '🌟', '✨', '🏁', '🧭', '💠', '🔆', '🪁', '🎪'];
const randomMarker = () => MARKER_GLYPHS[Math.floor(Math.random() * MARKER_GLYPHS.length)];

// How far through the current week/month/year we are right now, as a 0-100
// percentage. Pure date math - no backend data needed, always live.
const getTimeElapsedPct = (periodType) => {
  const now = new Date();
  const dayFraction = (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400;

  if (periodType === 'week') {
    return ((now.getDay() + dayFraction) / 7) * 100;
  }
  if (periodType === 'month') {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return (((now.getDate() - 1) + dayFraction) / daysInMonth) * 100;
  }
  if (periodType === 'year') {
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const daysInYear = (new Date(now.getFullYear() + 1, 0, 1) - startOfYear) / 86400000;
    const dayOfYear = Math.floor((now - startOfYear) / 86400000);
    return (((dayOfYear) + dayFraction) / daysInYear) * 100;
  }
  return 0;
};

// "Day 18 of 31" style label for the marker - concrete and immediately clear
const getDayOfPeriodLabel = (periodType) => {
  const now = new Date();
  if (periodType === 'week') {
    return `Day ${now.getDay() + 1} of 7`;
  }
  if (periodType === 'month') {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `Day ${now.getDate()} of ${daysInMonth}`;
  }
  if (periodType === 'year') {
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const daysInYear = (new Date(now.getFullYear() + 1, 0, 1) - startOfYear) / 86400000;
    const dayOfYear = Math.floor((now - startOfYear) / 86400000) + 1;
    return `Day ${dayOfYear} of ${daysInYear}`;
  }
  return '';
};

function PeriodRow({ label, data, periodType, bigPurchaseAmount = 0, includedBigPurchaseAmount = 0, annualDeficitAmount = 0, monthExhausted = false, nextMonthName = '' }) {
  const [marker] = useState(randomMarker); // picked once per mount = once per page load
  if (!data) return null;

  const barPct = Math.min(data.pct, 100);
  const timePct = Math.min(getTimeElapsedPct(periodType), 100);

  // Pace is now the single source of truth for status - are we spending
  // faster or slower than the calendar suggests we should be?
  const expectedSpend = (data.budget * timePct) / 100;
  const paceDiff = data.spent - expectedSpend;
  const paceThreshold = data.budget * 0.02; // within 2% of budget = "on pace"
  const paceState = paceDiff > paceThreshold ? 'over' : paceDiff < -paceThreshold ? 'under' : 'even';
  const c = PACE_COLORS[paceState];
  const paceDetail = paceState === 'even'
    ? 'Right on pace with the calendar'
    : `${fmt.currency(Math.abs(paceDiff))} ${paceState === 'over' ? 'over' : 'under'} pace`;

  // The day-marker label sits at timePct. If that's far enough right that it
  // would collide with a right-anchored status tag (especially on narrow
  // mobile widths), flip the tag to the left side instead.
  const tagOnLeft = timePct > 55;

  return (
    <div style={styles.periodRow}>
      <div style={styles.periodHeader}>
        <span style={styles.periodLabel}>{label}</span>
      </div>

      <div style={styles.amounts}>
        <span style={styles.spent}>{fmt.currency(data.spent)}</span>
        <span style={styles.divider}>/</span>
        <span style={styles.budgetAmt}>{fmt.currency(data.budget)}</span>
      </div>

      {data.deficitAmount > 0 && (
        <div style={styles.deficitNote}>
          Adaptive mode has $0 to allocate this {periodType} - prior spend this year is already {fmt.currency(annualDeficitAmount)} over your annual budget on its own.
        </div>
      )}

      {data.deficitAmount === 0 && periodType === 'week' && data.budget <= 0.01 && monthExhausted && (
        <div style={styles.deficitNote}>
          Month's budget is already spent - nothing left to allocate weekly until {nextMonthName}.
        </div>
      )}

      <div style={styles.barWrap}>
        <div style={styles.barTrack}>
          <div
            style={{
              ...styles.barFill,
              width: `${barPct}%`,
              background: c.bar,
              boxShadow: `0 0 8px ${c.bar}60`,
            }}
          />
          {/* Striped gap segment: the space between "where we should be" (time)
              and "where we are" (spend). Red stripes over the fill = spending
              ahead of pace. Green stripes over the empty track = cushion banked. */}
          {paceState === 'over' && (
            <div
              style={{
                ...styles.gapStripe,
                left: `${timePct}%`,
                width: `${barPct - timePct}%`,
                background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0, rgba(255,255,255,0.35) 3px, transparent 3px, transparent 7px)',
              }}
            />
          )}
          {paceState === 'under' && (
            <div
              style={{
                ...styles.gapStripe,
                left: `${barPct}%`,
                width: `${timePct - barPct}%`,
                background: 'repeating-linear-gradient(45deg, rgba(0,212,170,0.35) 0, rgba(0,212,170,0.35) 3px, transparent 3px, transparent 7px)',
              }}
            />
          )}
        </div>

        <div
          style={{ ...styles.paceMarker, left: `${timePct}%` }}
          title={`${timePct.toFixed(0)}% of ${periodType} elapsed`}
        >
          <span style={styles.paceMarkerGlyph}>{marker}</span>
          <div style={styles.paceMarkerLine} />
          <span style={styles.markerLabel}>{getDayOfPeriodLabel(periodType)}</span>
        </div>

        <div
          style={{
            ...styles.fillTag,
            ...(tagOnLeft ? { left: 0 } : { right: 0 }),
            color: c.text,
            borderColor: c.border,
            background: c.bg,
          }}
        >
          {paceState === 'over' && `🔺 ${fmt.currency(Math.abs(paceDiff))} over budget`}
          {paceState === 'under' && `🔻 ${fmt.currency(Math.abs(paceDiff))} under budget`}
          {paceState === 'even' && `🎯 On pace`}
        </div>
      </div>

      <div style={styles.remaining}>
        {data.remaining >= 0 ? (
          <span style={{ color: '#9ca3af' }}>
            <span style={{ color: c.text }}>{fmt.currency(Math.abs(data.remaining))}</span> remaining
          </span>
        ) : (
          <span style={{ color: '#ef4444' }}>
            {fmt.currency(Math.abs(data.remaining))} over budget
          </span>
        )}
        <span style={styles.pct}>{fmt.pct(data.pct)}</span>
      </div>

      <div style={styles.paceReadout}>
        {timePct.toFixed(0)}% of {periodType} elapsed · {barPct.toFixed(0)}% spent · {paceDetail}
      </div>

      {data.estimatedSpent > 0 && (
        <div style={styles.estimatedNote}>
          ~{fmt.currency(data.estimatedSpent)} estimated (no transaction history for some months) · {fmt.currency(data.actualSpent)} actual
        </div>
      )}

      {bigPurchaseAmount > 0 && (
        <div style={styles.bigPurchaseNote}>
          💥 Big purchases were {fmt.currency(bigPurchaseAmount)} in addition to what's shown above
        </div>
      )}

      {includedBigPurchaseAmount > 0 && (
        <div style={styles.bigPurchaseNoteMuted}>
          Includes {fmt.currency(includedBigPurchaseAmount)} in big purchases
        </div>
      )}
    </div>
  );
}

export default function BudgetWidget({ budget, annualBudget }) {
  const [collapsed, toggleCollapsed] = useCollapsible('budgetWidget');
  if (!budget) return <WidgetSkeleton />;

  const { week, month, year, projection, bigPurchases } = budget;

  return (
    <div style={styles.widget}>

      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>💰 Budget Overview</h2>
        </button>
        <span style={styles.budgetLabel}>
          {fmt.currency(annualBudget)}/yr
        </span>
      </div>

      {!collapsed && (
      <>
      <div style={styles.periods}>
        <PeriodRow
          label="This Week"
          data={week}
          periodType="week"
          annualDeficitAmount={year.remaining < 0 ? Math.abs(year.remaining) : 0}
          monthExhausted={month.spent >= month.budget}
          nextMonthName={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'long' })}
        />
        <PeriodRow label="This Month" data={month} periodType="month" bigPurchaseAmount={bigPurchases?.excludedFromBudget ? bigPurchases.thisMonth : 0} annualDeficitAmount={year.remaining < 0 ? Math.abs(year.remaining) : 0} />
        <PeriodRow label="This Year" data={year} periodType="year" includedBigPurchaseAmount={bigPurchases?.thisYear || 0} />
      </div>

      {projection && (
        <div style={{
          ...styles.projection,
          background: projection.onTrack ? 'rgba(0,212,170,0.05)' : 'rgba(239,68,68,0.05)',
          borderColor: projection.onTrack ? 'rgba(0,212,170,0.2)' : 'rgba(239,68,68,0.2)',
        }}>
          <div style={styles.projRow}>
            <span style={styles.projLabel}>
              {projection.onTrack ? '📈' : '📊'} Projected Year-End
            </span>
            <span style={{
              ...styles.projValue,
              color: projection.onTrack ? '#00d4aa' : '#ef4444',
            }}>
              {fmt.currency(projection.projectedYearEnd)}
            </span>
          </div>
          <div style={styles.projRow}>
            <span style={styles.projLabel}>Daily Burn Rate</span>
            <span style={styles.projValue}>{fmt.currency(projection.dailyBurnRate)}/day</span>
          </div>
          <div style={styles.projRow}>
            <span style={styles.projLabel}>
              {projection.onTrack ? 'Projected Savings' : 'Projected Overage'}
            </span>
            <span style={{
              ...styles.projValue,
              color: projection.onTrack ? '#00d4aa' : '#ef4444',
            }}>
              {projection.onTrack ? '+' : '-'}{fmt.currency(Math.abs(projection.projectedDelta))}
            </span>
          </div>
          <p style={styles.projNote}>
            {projection.onTrack
              ? `You're ${fmt.currency(Math.abs(projection.projectedDelta))} under budget at current pace.`
              : `At current pace you'll exceed your budget by ${fmt.currency(Math.abs(projection.projectedDelta))}.`}
          </p>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function WidgetSkeleton() {
  return (
    <div style={styles.widget}>
      <div style={{ ...styles.skeleton, height: 24, width: '60%', marginBottom: 24 }} />
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ marginBottom: 20 }}>
          <div style={{ ...styles.skeleton, height: 16, width: '40%', marginBottom: 8 }} />
          <div style={{ ...styles.skeleton, height: 8, width: '100%' }} />
        </div>
      ))}
    </div>
  );
}

const styles = {
  widget: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
    width: '0.8rem',
  },
  budgetLabel: {
    color: '#6b7280',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  periods: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  periodRow: {},
  periodHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.4rem',
  },
  periodLabel: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "'Syne', sans-serif",
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '20px',
    fontFamily: "'DM Mono', monospace",
  },
  amounts: { display: 'flex', alignItems: 'baseline', gap: '0.25rem', marginBottom: '0.5rem' },
  spent: {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'DM Mono', monospace",
  },
  divider: { color: '#374151', fontSize: '1rem' },
  budgetAmt: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontFamily: "'DM Mono', monospace",
  },
  barWrap: {
    position: 'relative',
    marginBottom: '0.4rem',
    paddingTop: '20px',
    paddingBottom: '18px',
  },
  barTrack: {
    position: 'relative',
    height: '6px',
    background: '#1e2a3a',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.6s ease',
  },
  gapStripe: {
    position: 'absolute',
    top: 0,
    height: '100%',
  },
  paceMarker: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 2,
  },
  paceMarkerGlyph: {
    fontSize: '1.1rem',
    lineHeight: 1,
    marginBottom: '2px',
    filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.9)) drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
  },
  paceMarkerLine: {
    width: '3px',
    flex: 1,
    background: 'linear-gradient(180deg, #fff, rgba(255,255,255,0.6))',
    borderRadius: '2px',
    boxShadow: '0 0 6px rgba(255,255,255,0.9)',
  },
  markerLabel: {
    fontSize: '0.6rem',
    color: '#e5e7eb',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    whiteSpace: 'nowrap',
    marginTop: '2px',
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
  },
  fillTag: {
    position: 'absolute',
    bottom: 0,
    border: '1px solid',
    borderRadius: '999px',
    padding: '1px 7px',
    fontSize: '0.62rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
    zIndex: 1,
  },
  paceReadout: {
    fontSize: '0.72rem',
    color: '#6b7280',
    fontFamily: "'DM Mono', monospace",
    marginTop: '0.5rem',
  },
  estimatedNote: {
    fontSize: '0.7rem',
    color: '#fbbf24',
    fontFamily: "'DM Mono', monospace",
    marginTop: '0.3rem',
  },
  deficitNote: {
    fontSize: '0.72rem',
    color: '#ef4444',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.4rem',
    lineHeight: 1.4,
  },
  bigPurchaseNote: {
    fontSize: '0.7rem',
    color: '#ef4444',
    fontFamily: "'DM Mono', monospace",
    marginTop: '0.3rem',
  },
  bigPurchaseNoteMuted: {
    fontSize: '0.68rem',
    color: '#6b7280',
    fontFamily: "'DM Mono', monospace",
    marginTop: '0.3rem',
  },
  remaining: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  pct: { color: '#6b7280' },
  projection: {
    marginTop: '1.5rem',
    border: '1px solid',
    borderRadius: '10px',
    padding: '1rem',
  },
  projRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.4rem',
  },
  projLabel: { color: '#9ca3af', fontSize: '0.8rem', fontFamily: "'Syne', sans-serif" },
  projValue: {
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  projNote: {
    color: '#6b7280',
    fontSize: '0.75rem',
    marginTop: '0.5rem',
    marginBottom: 0,
    fontStyle: 'italic',
  },
  skeleton: {
    background: 'linear-gradient(90deg, #1e2a3a 25%, #263347 50%, #1e2a3a 75%)',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};
